import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import '../styles/ProductDetail.css';

function ProductDetail({ onPreviewInvoice, onPreviewPurchaseInvoice, onPreviewSalesReturn, onPreviewPurchaseReturn }) {
    const { id } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const fetchProductHistory = async () => {
        try {
            setLoading(true);
            const data = await fetchApi(`/product/${id}/stock-history`);
            setProduct(data.product);
            setHistory(data.history || []);
        } catch (error) {
            console.error("Fetch Product Stock History Error:", error);
            if (error.status !== 403) {
                toast.error(`Failed to load product stock history: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchProductHistory();
        }
    }, [id]);

    // Format Date helper
    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };

    // Client-side search filtering
    const filteredHistory = useMemo(() => {
        if (!search) return history;
        const q = search.toLowerCase();
        return history.filter(item => 
            (item.invoice_no || '').toLowerCase().includes(q) ||
            (item.party || '').toLowerCase().includes(q) ||
            (item.section || '').toLowerCase().includes(q) ||
            String(item.date || '').includes(q)
        );
    }, [history, search]);

    // Client-side sorting
    const sortedHistory = useMemo(() => {
        let sortableItems = [...filteredHistory];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'stock_in' || sortConfig.key === 'stock_out' || sortConfig.key === 'closing_stock' || sortConfig.key === 'price' || sortConfig.key === 'total_amount') {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                } else if (sortConfig.key === 'date') {
                    aValue = a.timestamp || 0;
                    bValue = b.timestamp || 0;
                } else {
                    aValue = aValue ? aValue.toString().toLowerCase() : '';
                    bValue = bValue ? bValue.toString().toLowerCase() : '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredHistory, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Pagination calculations
    const totalEntries = sortedHistory.length;
    const totalPages = Math.ceil(totalEntries / pageSize) || 1;

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const pagedHistory = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return sortedHistory.slice(startIndex, startIndex + pageSize);
    }, [sortedHistory, page, pageSize]);

    const showingFrom = totalEntries > 0 ? (page - 1) * pageSize + 1 : 0;
    const showingTo = Math.min(page * pageSize, totalEntries);

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
    }

    // Export to Excel
    const handleExportExcel = () => {
        if (sortedHistory.length === 0) {
            toast.warn("No data to export");
            return;
        }

        const exportData = sortedHistory.map(item => ({
            'Invoice Date': formatDate(item.date),
            'Invoice No.': item.invoice_no || '-',
            'Party': item.party || 'N/A',
            'Section': item.section || '-',
            'Stock In': item.stock_in || 0,
            'Stock Out': item.stock_out || 0,
            'Closing Stock': item.closing_stock || 0
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Product Stock History');
        XLSX.writeFile(workbook, `${product?.product_name || 'Product'}_Stock_History.xlsx`);
    };

    // Print helper
    const handlePrint = () => {
        window.print();
    };

    // Handle preview document loading and callback
    const handlePreview = async (item) => {
        if (!item.refId || item.previewType === 'none') {
            toast.info("No preview available for this entry type.");
            return;
        }

        try {
            toast.info(`Fetching details for preview...`);
            if (item.previewType === 'purchase-invoice') {
                const data = await fetchApi(`/purchase-order/${item.refId}`);
                if (onPreviewPurchaseInvoice) onPreviewPurchaseInvoice(data);
            } else if (item.previewType === 'purchase-challan') {
                const data = await fetchApi(`/purchase-challan/${item.refId}`);
                if (onPreviewPurchaseInvoice) onPreviewPurchaseInvoice(data);
            } else if (item.previewType === 'sales-invoice') {
                const data = await fetchApi(`/sales-invoice/${item.refId}`);
                if (onPreviewInvoice) onPreviewInvoice(data);
            } else if (item.previewType === 'sales-challan') {
                const data = await fetchApi(`/sales-challan/${item.refId}`);
                if (onPreviewInvoice) onPreviewInvoice(data);
            } else if (item.previewType === 'purchase-return') {
                const data = await fetchApi(`/purchase-return/${item.refId}`);
                if (onPreviewPurchaseReturn) onPreviewPurchaseReturn(data);
            } else if (item.previewType === 'sales-return') {
                const data = await fetchApi(`/sales-return/${item.refId}`);
                if (onPreviewSalesReturn) onPreviewSalesReturn(data);
            }
        } catch (error) {
            console.error("Fetch transaction details error:", error);
            toast.error(`Failed to load preview: ${error.message}`);
        }
    };

    if (loading) {
        return <PageSkeleton />;
    }

    return (
        <div className="product-detail-container">
            {/* Header */}
            <div className="product-detail-header">
                <div className="product-detail-header-left">
                    <h1 className="product-detail-title">Product Transaction History</h1>
                </div>
                <div className="product-detail-action-buttons">
                    <button className="product-detail-back-btn" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                </div>
            </div>

            {/* Top 6 Info Cards */}
            <div className="product-info-grid">
                <div className="product-info-card">
                    <span className="product-info-card-label">Product Name</span>
                    <span className="product-info-card-value">{product?.product_name || '-'}</span>
                </div>
                <div className="product-info-card">
                    <span className="product-info-card-label">Product HSN Code</span>
                    <span className="product-info-card-value">{product?.product_hsn_code || '-'}</span>
                </div>
                <div className="product-info-card">
                    <span className="product-info-card-label">Category</span>
                    <span className="product-info-card-value">{product?.category_name || '-'}</span>
                </div>
                <div className="product-info-card">
                    <span className="product-info-card-label">MRP</span>
                    <span className="product-info-card-value">{(parseFloat(product?.product_sale_price) || 0).toFixed(2)}</span>
                </div>
                <div className="product-info-card">
                    <span className="product-info-card-label">Minimum</span>
                    <span className="product-info-card-value highlight-red">{product?.minimum_stock || 0}</span>
                </div>
                <div className="product-info-card">
                    <span className="product-info-card-label">Available</span>
                    <span className={`product-info-card-value ${parseFloat(product?.product_stock) <= parseFloat(product?.minimum_stock) ? 'highlight-red' : 'highlight-green'}`}>
                        {product?.product_stock || 0}
                    </span>
                </div>
            </div>

            {/* List and Table Area */}
            <div className="product-detail-content">
                <div className="product-detail-toolbar">
                    <SearchBar
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search by invoice no, party, section..."
                    />
                </div>

                <div className="product-detail-table-wrapper">
                    <table className="product-detail-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('date')} style={{ cursor: 'pointer' }}>
                                    INVOICE DATE {sortConfig.key === 'date' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('invoice_no')} style={{ cursor: 'pointer' }}>
                                    INVOICE NO. {sortConfig.key === 'invoice_no' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('party')} style={{ cursor: 'pointer' }}>
                                    PARTY {sortConfig.key === 'party' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('section')} style={{ cursor: 'pointer' }}>
                                    SECTIONS {sortConfig.key === 'section' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('stock_in')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                    STOCK IN {sortConfig.key === 'stock_in' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('stock_out')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                    STOCK OUT {sortConfig.key === 'stock_out' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th onClick={() => requestSort('closing_stock')} style={{ cursor: 'pointer', textAlign: 'right' }}>
                                    CLOSING STOCK {sortConfig.key === 'closing_stock' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                                </th>
                                <th style={{ textAlign: 'center' }}>PREVIEW</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedHistory.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                                        No transaction history found for this product.
                                    </td>
                                </tr>
                            ) : (
                                pagedHistory.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td>{formatDate(item.date)}</td>
                                        <td style={{ fontWeight: '500' }}>{item.invoice_no || '-'}</td>
                                        <td>{item.party}</td>
                                        <td>
                                            <span className={`section-pill ${item.section ? item.section.toLowerCase().replace(' ', '-') : ''}`}>
                                                {item.section}
                                            </span>
                                        </td>
                                        <td className="stock-cell in" style={{ textAlign: 'right' }}>
                                            {item.stock_in > 0 ? item.stock_in : '-'}
                                        </td>
                                        <td className="stock-cell out" style={{ textAlign: 'right' }}>
                                            {item.stock_out > 0 ? item.stock_out : '-'}
                                        </td>
                                        <td className="stock-cell closing" style={{ textAlign: 'right' }}>
                                            {item.closing_stock}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {item.previewType !== 'none' ? (
                                                <button 
                                                    className="btn-preview-action"
                                                    onClick={() => handlePreview(item)}
                                                >
                                                    Preview
                                                </button>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="product-detail-footer">
                    <div className="product-detail-entries">
                        <span>Show</span>
                        <select
                            className="product-detail-entries-select"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>

                    <div className="product-detail-info">
                        Showing {showingFrom} to {showingTo} of {totalEntries} entries
                    </div>

                    <div className="product-detail-pagination">
                        <button
                            type="button"
                            className="product-detail-page-btn"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </button>

                        {pages.map((p) => (
                            <button
                                key={p}
                                type="button"
                                className={`product-detail-page-num ${p === page ? 'product-detail-page-num-active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        ))}

                        <button
                            type="button"
                            className="product-detail-page-btn"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Print Only Table view with complete records */}
            <div className="printable-product-table print-only">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px' }}>DATE</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px' }}>INVOICE NO.</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px' }}>PARTY</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px' }}>SECTION</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>STOCK IN</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>STOCK OUT</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>CLOSING STOCK</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedHistory.map((item, idx) => (
                            <tr key={idx}>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px' }}>{formatDate(item.date)}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px' }}>{item.invoice_no || '-'}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px' }}>{item.party}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px' }}>{item.section}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>{item.stock_in || '-'}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>{item.stock_out || '-'}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right' }}>{item.closing_stock}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ProductDetail;
